import { useMemo } from "react";
import PropTypes from "prop-types";

import { alignItems, colors, strCode } from "../../../../../consts";
import { t } from "../../../../../services";
import { dateUtil } from "../../../../../utils";
import { adminRolesConfigUtils } from "../../utils/adminRolesConfigUtils";

import {
  FlexContainers,
  IconButton,
  Paragraph2,
  SmallButton,
  SVG,
  WrapperContainer
} from "../../../../../components";
import { ButtonsWrapper, RoleCardWrapper, RoleDateInfo, Subtitle, Title } from "./styled";

export const RoleCard = ({
  roleConfig,
  hasError,
  onDeleteConfig = () => {},
  onEditConfig = () => {}
}) => {
  const locationInfo = useMemo(() => {
    const location = roleConfig?.location?.value;
    const markets = roleConfig?.market?.map((m) => m?.value)?.join(", ");

    return location ? `${markets} - ${location}` : markets;
  }, [roleConfig]);

  const validUntil = useMemo(() => {
    return adminRolesConfigUtils.formatDate(roleConfig?.to) ?? null;
  }, [roleConfig]);

  const isExipred = useMemo(() => {
    if (roleConfig?.to) {
      const dateDiff = dateUtil.getDateDifferenceInMilliseconds(new Date(), roleConfig?.to);

      return dateDiff > 0;
    }

    return false;
  }, [roleConfig]);

  return (
    <RoleCardWrapper
      Container={FlexContainers.FlexColumnBetween}
      alignItems={alignItems.start}
      gap={16}
      hasError={hasError}>
      <WrapperContainer
        Container={FlexContainers.FlexColumnStart}
        alignItems={alignItems.start}
        gap={16}>
        <WrapperContainer Container={FlexContainers.FlexColumnStart} alignItems={alignItems.start}>
          <Title>{roleConfig?.application?.value}</Title>
          <Subtitle hasError={hasError}>{roleConfig?.role?.value}</Subtitle>
        </WrapperContainer>

        <WrapperContainer
          Container={FlexContainers.FlexColumnStart}
          alignItems={alignItems.start}
          gap={4}>
          <WrapperContainer Container={FlexContainers.FlexRowStart} gap={4}>
            <SVG.LocationIcon />
            <Paragraph2>{locationInfo}</Paragraph2>
          </WrapperContainer>
          {validUntil && (
            <RoleDateInfo isExipred={isExipred}>
              {isExipred
                ? t(strCode.EXPIRED_INFO, { validUntil })
                : t(strCode.VALID_UNTIL_INFO, { validUntil })}
            </RoleDateInfo>
          )}
        </WrapperContainer>
      </WrapperContainer>

      <ButtonsWrapper Container={FlexContainers.FlexRowBetween} gap={16} width={"100%"}>
        <IconButton hasHoverCircle onClick={() => onDeleteConfig(roleConfig)}>
          <SVG.DeleteIcon color={colors.ERROR} />
        </IconButton>

        <SmallButton secondary onClick={() => onEditConfig(roleConfig)}>
          <SVG.EditIcon />
          {t(strCode.EDIT)}
        </SmallButton>
      </ButtonsWrapper>
    </RoleCardWrapper>
  );
};

RoleCard.propTypes = {
  roleConfig: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    application: PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    }),
    market: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.string.isRequired, value: PropTypes.string.isRequired })
    ).isRequired,
    location: PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    }),
    role: PropTypes.shape({ id: PropTypes.string.isRequired, value: PropTypes.string.isRequired }),
    from: PropTypes.instanceOf(Date),
    to: PropTypes.instanceOf(Date)
  }).isRequired,
  hasError: PropTypes.bool,
  onDeleteConfig: PropTypes.func.isRequired,
  onEditConfig: PropTypes.func.isRequired
};
